import { useUIStore } from '@/config/stores/ui-store';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { Conversation } from '@/features/conversation';
import { MessageContentType, setReadMarker } from '@/features/messages';
import { MessageReactionDialog } from '@/features/messages/components/message-reaction-dialog';
import { MEDIA_LAYOUT_CONSTANTS } from '@/features/messages/utils/image-layout-utils';
import { ErrorBoundary } from '@/shared/components/error-boundary';
import { cn } from '@/shared/utilities';
import { ArrowDownward } from '@mui/icons-material';
import { Box, Chip, Skeleton } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoProvider } from 'react-photo-view';
import { useActionWorker } from '../hooks/use-action-worker';
import { useInfiniteScrollEdges } from '../hooks/use-infinite-scroll-edges';
import { useMessageEvents } from '../hooks/use-message-events';
import { useInvalidateMessages, useMessageList } from '../queries/use-message-list';
import {
  buildMeasurementsStorageKeys,
  clearSaveTimer,
  createMeasurementsOnChange,
  getInitialMeasurementsCache
} from '../utils/measurements-cache';
import { MessageEmpty } from './message-list.empty';
import { MessageListError } from './message-list.error';
import { MessageListItem } from './message-list.item';

export interface MessageListProps {
  conversationId: string;
  mode?: 'compact' | 'comfortable';
  height?: number | string;
  className?: string;
}

export const MessageList: FC<MessageListProps> = ({
  conversationId,
  className,
  height = '100%',
  mode = 'comfortable'
}) => {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.user_id);
  const invalidateMessages = useInvalidateMessages();

  const {
    query,
    messages,
    isFetching,
    isError,
    canLoadOlder,
    loadOlder,
    isLoadingOlder,
    canLoadNewer,
    loadNewer,
    isLoadingNewer
  } = useMessageList({
    conversationId,
    enabled: Boolean(conversationId)
  });

  const parentRef = useRef<HTMLDivElement | null>(null);
  const saveTimerRef = useRef<number | null>(null);

  const storageKeys = useMemo(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 0;
    const bucket = Math.max(0, Math.round(width / 100) * 100);
    return buildMeasurementsStorageKeys(`${conversationId}:${mode}:${bucket}`);
  }, [conversationId, mode]);

  const virtualizer = useVirtualizer({
    count: canLoadOlder || canLoadNewer ? messages.length + 1 : messages.length,
    estimateSize: (idx) => {
      const msg = messages[idx];
      if (msg?.contentType === MessageContentType.COMPOSE) {
        // Use actual MediaLayout heights
        const mediaConfig = MEDIA_LAYOUT_CONSTANTS[mode];
        const hasMedia = msg.structuredAttachments?.some((a) => a.contentType === 'MEDIA');
        if (hasMedia) {
          // Single vs multi handled by MediaLayout, use max bound
          return mediaConfig.single.maxHeight + 80; // +80 for text/padding
        }
        return 120; // Compose without media
      }
      return 80; // Text message
    },
    getScrollElement: () => parentRef.current,
    getItemKey: (index) => messages[index]?.messageId || index,
    overscan: 20,
    onChange: createMeasurementsOnChange(storageKeys, saveTimerRef),
    initialMeasurementsCache: getInitialMeasurementsCache(storageKeys) as any,
    gap: 4,
    indexAttribute: 'data-index'
  });

  useEffect(
    () => () => {
      clearSaveTimer(saveTimerRef);
    },
    []
  );

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const viewportH = parentRef.current?.clientHeight ?? 0;
  const offset = Math.max(0, viewportH - totalSize);

  const { isAtBottomRef } = useInfiniteScrollEdges({
    scrollContainerRef: parentRef,
    canLoadOlder,
    isLoadingOlder,
    loadOlder,
    canLoadNewer,
    isLoadingNewer,
    loadNewer
  });

  useMessageEvents();
  useActionWorker({ conversationId, messages, query, virtualizer });

  const { activeConversation, setActiveConversation } = useUIStore();

  // Track read marker when user is viewing messages in this conversation
  // This handles both initial load and real-time message updates
  useEffect(() => {
    if (!messages.length) return;

    // Only track if this is the active conversation
    if (activeConversation?.conversationId !== conversationId) return;

    const lastMessage = messages.at(-1);
    if (!lastMessage?.messageId || !lastMessage.sentAt || lastMessage.isSending || userId === lastMessage.senderId)
      return;

    const lastSeenTimestamp = activeConversation.lastSeenTimestamp;
    const messageSentAt = lastMessage.sentAt;

    // Update if no lastSeenTimestamp exists or if the last message timestamp is newer
    const shouldUpdate = !lastSeenTimestamp || dayjs(messageSentAt).isAfter(dayjs(lastSeenTimestamp));

    if (shouldUpdate) {
      // Call API to update read marker on server
      setReadMarker(conversationId, lastMessage.messageId);

      // Update the active conversation in UI store
      const updatedConversation = new Conversation({
        ...activeConversation,
        lastSeenMessageId: lastMessage.messageId,
        lastSeenTimestamp: dayjs(messageSentAt).toISOString(),
        unreadCount: 0
      });

      setActiveConversation(updatedConversation);
    }
  }, [conversationId, messages, activeConversation, setActiveConversation, userId]);

  return (
    <ErrorBoundary onRetry={() => invalidateMessages(conversationId!)}>
      <PhotoProvider>
        <Box
          className={className}
          height={height}
          display='flex'
          flexDirection='column'
          overflow='hidden'
          position='relative'
        >
          {isLoadingOlder && (
            <Box position='absolute' top={8} left='50%' sx={{ transform: 'translateX(-50%)', zIndex: 1000 }}>
              <Chip size='small' color='default' label={t('messageList.loading')} />
            </Box>
          )}

          <Box
            component='div'
            ref={parentRef}
            id={`message-list-${conversationId}`}
            className='mex:my-2 mex:flex mex:h-full mex:overflow-auto mex:overflow-x-hidden'
            role='log'
            aria-live='polite'
            aria-relevant='additions'
            aria-label={t('messages.aria.chatLog')}
          >
            <Box component='div' sx={{ height: Math.max(totalSize, viewportH), position: 'relative', width: '100%' }}>
              {isError && <MessageListError onRefresh={() => invalidateMessages(conversationId)} />}

              {(query.isRefetching || query.isFetching) && !isError && (
                <Box p={2} display='flex' flexDirection='column' gap={1.5}>
                  {['s1', 's2', 's3', 's4', 's5', 's6'].map((id, i) => (
                    <Skeleton
                      key={id}
                      variant='rounded'
                      height={68}
                      width={`${60 + (i % 3) * 10}%`}
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                </Box>
              )}
              {!messages.length && !isFetching && !isError && <MessageEmpty mode={mode} />}
              {items.map((item) => {
                const m = messages[item.index];
                return (
                  <Box
                    id={`${conversationId}-${item.key}`}
                    key={`${conversationId}-${item.key}-${item.index}`}
                    ref={virtualizer.measureElement}
                    style={{
                      top: 0,
                      position: 'absolute',
                      transform: `translateY(${item.start + offset}px)`
                    }}
                    className='mex:w-full mex:px-0'
                    data-index={item.index}
                    tabIndex={-1}
                  >
                    {m && (
                      <MessageListItem
                        mode={mode}
                        message={m}
                        curUserId={userId}
                        isLast={item.index === messages.length - 1}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {isLoadingNewer && (
            <Box position='absolute' bottom={8} left='50%' sx={{ transform: 'translateX(-50%)', zIndex: 1000 }}>
              <Chip size='small' color='default' label={t('messageList.loading')} />
            </Box>
          )}

          {!isAtBottomRef.current && messages.length > 0 && (
            <Box
              id='down-button'
              component='button'
              onClick={() => virtualizer.scrollToOffset(totalSize, { behavior: 'auto' })}
              className={cn(
                'mex:absolute mex:right-10 mex:bottom-4 mex:flex mex:translate-x-1/2 mex:animate-bounce mex:cursor-pointer mex:items-center mex:justify-center mex:rounded-full mex:bg-primary mex:shadow-md',
                'mex:size-7'
              )}
            >
              <ArrowDownward className='mex:size-6 mex:text-white' />
            </Box>
          )}
        </Box>
      </PhotoProvider>
      <MessageReactionDialog conversationId={conversationId} />
    </ErrorBoundary>
  );
};

export default MessageList;
